import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Home, MapPin, Plus, Star, Check } from "lucide-react";
import { axiosInstance } from "@/App";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const HomeSelector = ({ 
  selectedHomeId, 
  onHomeSelect, 
  showAddButton = true,
  className = "",
  label = "Select Home for Service",
  variant = "full", // "full" = dropdown with details, "compact" = smaller for header
  onHomesLoaded = null // Callback when homes are loaded
}) => {
  const [homes, setHomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    fetchHomes();
  }, []);

  const fetchHomes = async () => {
    try {
      const response = await axiosInstance.get("/users/homes");
      const fetchedHomes = response.data.homes || [];
      setHomes(fetchedHomes);
      
      // Notify parent of loaded homes
      if (onHomesLoaded) {
        onHomesLoaded(fetchedHomes);
      }
      
      // Auto-select primary home if none selected
      if (!selectedHomeId && fetchedHomes.length > 0) {
        const primaryHome = fetchedHomes.find(h => h.is_primary) || fetchedHomes[0];
        if (primaryHome && onHomeSelect) {
          onHomeSelect(primaryHome.id, primaryHome);
        }
      }
    } catch (error) {
      console.error("Error fetching homes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (homeId) => {
    const selectedHome = homes.find(h => h.id === homeId);
    
    // If selecting a different home, set it as primary via API
    if (selectedHome && !selectedHome.is_primary) {
      setSwitching(true);
      try {
        await axiosInstance.put(`/users/homes/${homeId}/set-primary`);
        
        // Update local state
        setHomes(prev => prev.map(h => ({
          ...h,
          is_primary: h.id === homeId
        })));
        
        toast.success(`Switched to ${selectedHome.nickname || selectedHome.address}`);
      } catch (error) {
        console.error("Error setting primary home:", error);
        toast.error("Failed to switch home");
        setSwitching(false);
        return;
      } finally {
        setSwitching(false);
      }
    }
    
    if (onHomeSelect) {
      onHomeSelect(homeId, selectedHome);
    }
  };

  // Compact variant for header
  if (variant === "compact") {
    if (loading) {
      return (
        <div className={`flex items-center gap-1.5 text-gray-500 ${className}`}>
          <Home className="w-4 h-4 animate-pulse" />
        </div>
      );
    }

    if (homes.length === 0) {
      return (
        <Link to="/profile" className={`flex items-center gap-1.5 text-amber-600 hover:text-amber-700 text-sm ${className}`}>
          <Home className="w-4 h-4" />
          <span className="hidden sm:inline">Add Home</span>
        </Link>
      );
    }

    const primaryHome = homes.find(h => h.is_primary) || homes[0];
    const displayName = primaryHome.nickname || 
      (primaryHome.address ? primaryHome.address.split(',')[0] : 'Home');

    if (homes.length === 1) {
      return (
        <div className={`flex items-center gap-1.5 text-gray-700 text-sm ${className}`} data-testid="header-home-single">
          <Home className="w-4 h-4 text-orange-500" />
          <span className="hidden sm:inline truncate max-w-[120px]">{displayName}</span>
        </div>
      );
    }

    // Multiple homes - compact dropdown
    return (
      <Select value={selectedHomeId || primaryHome.id} onValueChange={handleSelect} disabled={switching}>
        <SelectTrigger 
          className={`h-8 border-0 bg-transparent hover:bg-orange-50 gap-1.5 px-2 min-w-0 ${className}`} 
          data-testid="header-home-selector"
        >
          <Home className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <span className="hidden sm:inline truncate max-w-[100px] text-sm">
            {switching ? "Switching..." : (homes.find(h => h.id === (selectedHomeId || primaryHome.id))?.nickname || 
              homes.find(h => h.id === (selectedHomeId || primaryHome.id))?.address?.split(',')[0] || 'Home')}
          </span>
        </SelectTrigger>
        <SelectContent align="end">
          <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase">
            Switch Property
          </div>
          {homes.map((home) => (
            <SelectItem key={home.id} value={home.id} className="cursor-pointer">
              <div className="flex items-center gap-2">
                {home.is_primary ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Home className="w-4 h-4 text-gray-400" />
                )}
                <div className="flex flex-col">
                  <span className="truncate max-w-[180px] text-sm">
                    {home.nickname || home.address?.split(',')[0] || 'Home'}
                  </span>
                  {home.nickname && (
                    <span className="text-xs text-gray-500 truncate max-w-[180px]">
                      {home.address?.split(',')[0]}
                    </span>
                  )}
                </div>
              </div>
            </SelectItem>
          ))}
          <div className="border-t mt-1 pt-1">
            <Link to="/profile" className="block">
              <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-orange-600 hover:bg-orange-50 rounded cursor-pointer">
                <Plus className="w-4 h-4" />
                {homes.length < 5 ? "Add Another Home" : "Manage Homes"}
              </div>
            </Link>
          </div>
        </SelectContent>
      </Select>
    );
  }

  // Full variant (default) for calculator pages
  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-gray-500 ${className}`}>
        <Home className="w-4 h-4 animate-pulse" />
        <span className="text-sm">Loading homes...</span>
      </div>
    );
  }

  if (homes.length === 0) {
    return (
      <div className={`bg-amber-50 border border-amber-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <Home className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">No homes added yet</p>
            <p className="text-xs text-amber-600">Add your home address to get started</p>
          </div>
          <Link to="/profile">
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
              <Plus className="w-4 h-4 mr-1" />
              Add Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // If only one home, show it as selected without dropdown
  if (homes.length === 1) {
    const home = homes[0];
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-3 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <Home className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 truncate">
                {home.nickname || home.address}
              </p>
              {home.is_primary && (
                <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                  <Star className="w-3 h-3 mr-1" />
                  Primary
                </Badge>
              )}
            </div>
            {home.nickname && (
              <p className="text-xs text-gray-500 truncate">{home.address}</p>
            )}
            {home.profile_completion !== undefined && (
              <p className="text-xs text-gray-500">
                Profile {home.profile_completion}% complete
              </p>
            )}
          </div>
          {showAddButton && homes.length < 5 && (
            <Link to="/profile">
              <Button variant="ghost" size="sm" className="text-gray-500">
                <Plus className="w-4 h-4" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Multiple homes - show dropdown
  const currentHome = homes.find(h => h.id === selectedHomeId) || homes.find(h => h.is_primary) || homes[0];
  
  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-orange-500" />
        {label}
      </label>
      <div className="flex gap-2">
        <Select value={selectedHomeId || currentHome?.id} onValueChange={handleSelect} disabled={switching}>
          <SelectTrigger className="flex-1" data-testid="home-selector">
            <SelectValue placeholder="Select a home">
              {currentHome && (
                <div className="flex items-center gap-2 truncate">
                  <Home className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="truncate">
                    {switching ? "Switching..." : (currentHome.nickname || currentHome.address)}
                  </span>
                  {currentHome.is_primary && (
                    <Star className="w-3 h-3 text-orange-500 flex-shrink-0" />
                  )}
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {homes.map((home) => (
              <SelectItem key={home.id} value={home.id}>
                <div className="flex items-center gap-2">
                  {home.is_primary ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Home className="w-4 h-4 text-gray-400" />
                  )}
                  <div className="flex flex-col">
                    <span className="truncate max-w-[200px]">
                      {home.nickname || home.address}
                    </span>
                    {home.nickname && (
                      <span className="text-xs text-gray-500 truncate max-w-[200px]">
                        {home.address?.split(',')[0]}
                      </span>
                    )}
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showAddButton && homes.length < 5 && (
          <Link to="/profile">
            <Button variant="outline" size="icon" title="Add another home">
              <Plus className="w-4 h-4" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default HomeSelector;
